

import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent } from '@/components/ui/card';

const PrivacyPolicy = () => {
  return (
    <section id="privacy" className="mb-16">
      <h2 className="text-2xl font-bold mb-6 pb-2 border-b border-neutral-200 text-left">Privacy Policy</h2>
      
      <Card className="border shadow-sm">
        <CardContent className="p-0">
          <ScrollArea className="h-[400px] rounded-md">
            <div className="p-6 text-left">
              <p className="text-neutral-600 mb-4">
                Team Jumo, Inc., doing business as The Mat Monster ("we," "our," or "us"), respects your privacy and is committed to protecting it. 
                This Privacy Policy explains how we collect, use, share, and protect your information when you use our mobile app, website, and services (collectively, "The Mat Monster" or "the app").
              </p>
              <p className="text-neutral-600 mb-6">
                By using The Mat Monster, you agree to the practices described in this Privacy Policy. If you do not agree, please do not use the app.
              </p>
              
              <h3 className="text-xl font-semibold mt-6 mb-3">1. What We Collect</h3>
              
              <h4 className="text-lg font-medium mt-4 mb-2">1.1 Personal Information</h4>
              <p className="text-neutral-600 mb-2">We may collect:</p>
              <ul className="list-disc pl-6 mb-4 text-neutral-600">
                <li>Name or display name</li>
                <li>Email address</li>
                <li>Account credentials</li>
                <li>Notes, journal entries, or training logs</li>
                <li>Linked content (e.g., YouTube URLs)</li>
                <li>Subscription or purchase history</li>
                <li>Device identifiers, IP address, or general location</li>
              </ul>
              <p className="text-neutral-600 mb-4">We do not collect or store audio, video, or image files directly at this time.</p>
              
              <h4 className="text-lg font-medium mt-4 mb-2">1.2 Usage Data</h4>
              <p className="text-neutral-600 mb-2">We collect data about how you use the app, including:</p>
              <ul className="list-disc pl-6 mb-4 text-neutral-600">
                <li>Features used, time spent, and techniques interacted with</li>
                <li>Browser or device type</li>
                <li>Operating system and app version</li>
              </ul>
              <p className="text-neutral-600 mb-6">When analyzing trends, we use aggregated or anonymized data that cannot reasonably identify you.</p>
              
              <h3 className="text-xl font-semibold mt-6 mb-3">2. How We Use Your Information</h3>
              <p className="text-neutral-600 mb-2">We use your information to:</p>
              <ul className="list-disc pl-6 mb-4 text-neutral-600">
                <li>Provide and personalize your experience</li>
                <li>Save your training progress and personal data</li>
                <li>Improve app functionality and design</li>
                <li>Generate insights based on your journal entries using trusted AI processors (e.g., OpenAI) </li>
                <li>Process payments and manage subscriptions</li>
                <li>Communicate with you about your account or updates</li>
              </ul>
              <p className="text-neutral-600 mb-6">Whenever possible, we anonymize or aggregate data so it cannot be tied back to you personally.</p>
              
              <h3 className="text-xl font-semibold mt-6 mb-3">3. Cookies & Similar Technologies</h3>
              <p className="text-neutral-600 mb-2">We use cookies and similar technologies to:</p>
              <ul className="list-disc pl-6 mb-4 text-neutral-600">
                <li>Keep you signed in</li>
                <li>Remember your preferences</li>
                <li>Track usage trends</li>
              </ul>
              <p className="text-neutral-600 mb-6">You can manage cookies in your device or browser settings. See our Cookie Policy for full details.</p>
              
              <h3 className="text-xl font-semibold mt-6 mb-3">4. Sharing Your Data</h3>
              <p className="text-neutral-600 mb-2">We do not sell, rent, or trade your personal information. Ever.</p>
              <p className="text-neutral-600 mb-2">We only share data:</p>
              <ul className="list-disc pl-6 mb-4 text-neutral-600">
                <li>With trusted service providers (e.g., hosting, analytics, AI processors, or payment platforms) who help us operate, improve, and provide our services</li>
                <li>When legally required</li>
                <li>In a business transfer, like a merger or acquisition</li>
              </ul>
              <p className="text-neutral-600 mb-6">Some journal entries and personal training notes you create may be temporarily sent to our trusted AI processing partner (OpenAI) to generate insights or summaries. These insights are used to improve your training experience. We do not use this content for advertising, tracking, or to build marketing profiles. All partners are bound by strict confidentiality and data protection obligations, and we never allow them to use your data for any other purpose.
              </p>
              
              <h3 className="text-xl font-semibold mt-6 mb-3">5. User-Generated Content</h3>
              <p className="text-neutral-600 mb-4">
                You may create and submit content like personal notes or linked materials. In the future, you may upload video instructionals or other original material.
              </p>
              <p className="text-neutral-600 mb-4">
                If you choose to upload instructional material or content intended for monetization, that content may be made visible to others and used within the platform for educational, promotional, or commercial purposes.
              </p>
              <p className="text-neutral-600 mb-6">
                You may be eligible to earn a commission or share in revenue from any purchases or interactions with your content, subject to separate terms. However, monetization is not guaranteed, and The Mat Monster reserves the right to approve, moderate, or remove content at its discretion.
              </p>
              
              <h3 className="text-xl font-semibold mt-6 mb-3">6. Data Retention</h3>
              <p className="text-neutral-600 mb-6">
                We retain data only as long as necessary to provide our services, comply with legal obligations, or fulfill user requests. You may request deletion of your account and data at any time.
              </p>
              
              <h3 className="text-xl font-semibold mt-6 mb-3">7. Security</h3>
              <p className="text-neutral-600 mb-6">
                We use industry-standard encryption, secure servers, and internal access restrictions. While no system is 100% secure, we work hard to protect your information.
              </p>
              
              <h3 className="text-xl font-semibold mt-6 mb-3">8. Children's Privacy</h3>
              <p className="text-neutral-600 mb-6">
                This app is intended for users aged 13 and older. We do not knowingly collect personal data from anyone under 13. If we learn that we have, we'll delete it promptly.
              </p>
              
              <h3 className="text-xl font-semibold mt-6 mb-3">9. International Users</h3>
              <p className="text-neutral-600 mb-6">
                Your data may be stored and processed in the United States, where our servers are located. By using the app, you consent to this transfer and understand that U.S. privacy standards may differ from those in your country.
              </p>
              
              <h3 className="text-xl font-semibold mt-6 mb-3">10. Your Rights</h3>
              <p className="text-neutral-600 mb-2">Depending on your location, you may have the right to:</p>
              <ul className="list-disc pl-6 mb-4 text-neutral-600">
                <li>Access your data</li>
                <li>Request correction or deletion</li>
                <li>Withdraw consent</li>
                <li>Object to certain uses</li>
              </ul>
              <p className="text-neutral-600 mb-6">
                Contact us at oss@thematmonster.com to submit a request.
              </p>
              
              <h3 className="text-xl font-semibold mt-6 mb-3">11. Updates</h3>
              <p className="text-neutral-600 mb-6">
                We may update this Privacy Policy from time to time. If changes are significant, we'll notify you through the app or by email.
              </p>
              
              <h3 className="text-xl font-semibold mt-6 mb-3">12. Contact Us</h3>
              <p className="text-neutral-600 mb-2">The Mat Monster</p>
              <p className="text-neutral-600 mb-2">Email: oss@thematmonster.com</p>
              <p className="text-neutral-600 mb-6">Mailing Address: 500 E Whitestone BLVD Unit 902 Cedar Park, TX 78630</p>
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </section>
  );
};

export default PrivacyPolicy;
